import type { ComponentProps, ReactNode } from 'react';
import { cx } from '../utils.ts';
import { Kbd } from './Kbd.tsx';
import { Spinner } from './Feedback.tsx';
import { Tooltip } from './Tooltip.tsx';
import './button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'soft' | 'outline' | 'danger' | 'danger-soft';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. */
  icon?: ReactNode;
  /** Trailing icon. */
  iconRight?: ReactNode;
  loading?: boolean;
  /** Full width. */
  block?: boolean;
  /** Shortcut hint rendered inside the button, e.g. `mod+enter`. */
  shortcut?: string;
  /** Selected/pressed look for toggle buttons. */
  active?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  loading,
  block,
  shortcut,
  active,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx('g-btn', block && 'g-btn--block', className)}
      data-variant={variant}
      data-size={size}
      data-active={active || undefined}
      data-loading={loading || undefined}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 16 : 14} /> : icon}
      {children !== undefined && children !== null && <span className="g-btn__label">{children}</span>}
      {iconRight}
      {shortcut && <Kbd shortcut={shortcut} size="sm" className="g-btn__kbd" />}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'iconRight' | 'children'> {
  /** Accessible label; also shown as a tooltip unless `tooltip={false}`. */
  label: string;
  children: ReactNode;
  tooltip?: boolean | ReactNode;
  tooltipShortcut?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
}

export function IconButton({
  label,
  children,
  variant = 'ghost',
  size = 'md',
  tooltip = true,
  tooltipShortcut,
  tooltipPlacement,
  className,
  ...rest
}: IconButtonProps) {
  const button = (
    <Button variant={variant} size={size} aria-label={label} className={cx('g-btn--icon', className)} icon={children} {...rest} />
  );
  if (!tooltip) return button;
  return (
    <Tooltip content={tooltip === true ? label : tooltip} shortcut={tooltipShortcut} placement={tooltipPlacement}>
      {button}
    </Tooltip>
  );
}

export function ButtonGroup({ className, ...rest }: ComponentProps<'div'>) {
  return <div role="group" className={cx('g-btn-group', className)} {...rest} />;
}
